/*
 * TemplateModal.jsx
 * -----------------
 * Modal that presents pre-built checklist templates.
 * User picks a template, previews its items, then creates
 * a checklist from it with one click.
 * Templates are defined locally -- no API call needed to load them.
 * Creating from a template calls the checklists API.
 * Used on the Checklists page.
 */

import React, { useState } from "react";
import { useAuth } from "../App";
import CategoryTag from "./CategoryTag";

// ============================================
// TEMPLATE DEFINITIONS
// ============================================
const TEMPLATES = [
  {
    id: "moving",
    title: "Moving Checklist",
    description: "Everything you need to plan and execute a move.",
    category: "Home",
    icon: "📦",
    recurring: false,
    items: [
      { text: "Research moving companies", priority: "high" },
      { text: "Get moving quotes", priority: "high" },
      { text: "Book moving company", priority: "high" },
      { text: "Notify landlord or sell current home", priority: "high" },
      { text: "Change address with USPS", priority: "medium" },
      { text: "Transfer utilities to new address", priority: "medium" },
      { text: "Update address with bank and credit cards", priority: "medium" },
      { text: "Update address with employer", priority: "medium" },
      { text: "Pack non-essentials first", priority: "low" },
      { text: "Label all boxes by room", priority: "medium" },
      { text: "Pack essentials bag for moving day", priority: "high" },
      { text: "Clean old home after move", priority: "low" },
      { text: "Set up internet at new home", priority: "medium" },
      { text: "Update drivers license address", priority: "low" },
    ]
  },
  {
    id: "weekly-cleaning",
    title: "Weekly Cleaning",
    description: "Keep your home spotless with a weekly routine.",
    category: "Home",
    icon: "🧹",
    recurring: true,
    recurringInterval: "weekly",
    items: [
      { text: "Vacuum all rooms", priority: "medium" },
      { text: "Mop hard floors", priority: "medium" },
      { text: "Clean bathrooms", priority: "high" },
      { text: "Wipe down kitchen counters", priority: "high" },
      { text: "Clean kitchen appliances", priority: "low" },
      { text: "Take out trash", priority: "high" },
      { text: "Change bed sheets", priority: "medium" },
      { text: "Wipe mirrors and windows", priority: "low" },
      { text: "Declutter common areas", priority: "low" },
    ]
  },
  {
    id: "grocery-run",
    title: "Grocery Run",
    description: "A reusable grocery checklist you can reset each week.",
    category: "Shopping",
    icon: "🛒",
    recurring: true,
    recurringInterval: "weekly",
    items: [
      { text: "Fruits and vegetables", priority: "high" },
      { text: "Bread and grains", priority: "medium" },
      { text: "Dairy and eggs", priority: "high" },
      { text: "Meat and protein", priority: "high" },
      { text: "Frozen foods", priority: "low" },
      { text: "Snacks", priority: "low" },
      { text: "Beverages", priority: "medium" },
      { text: "Condiments and sauces", priority: "low" },
      { text: "Cleaning supplies", priority: "medium" },
      { text: "Paper products", priority: "medium" },
    ]
  },
  {
    id: "morning-routine",
    title: "Morning Routine",
    description: "Start every day right with a consistent morning.",
    category: "Personal",
    icon: "☀️",
    recurring: true,
    recurringInterval: "daily",
    items: [
      { text: "Wake up on time", priority: "high" },
      { text: "Drink a glass of water", priority: "high" },
      { text: "Exercise or stretch", priority: "medium" },
      { text: "Shower", priority: "high" },
      { text: "Eat breakfast", priority: "high" },
      { text: "Review today's checklist", priority: "medium" },
      { text: "Check calendar for the day", priority: "medium" },
      { text: "Meditate or journal", priority: "low" },
    ]
  },
  {
    id: "work-week",
    title: "Work Week Prep",
    description: "Get ready for a productive week every Monday.",
    category: "Work",
    icon: "💼",
    recurring: true,
    recurringInterval: "weekly",
    items: [
      { text: "Review last week's unfinished tasks", priority: "high" },
      { text: "Set this week's priorities", priority: "high" },
      { text: "Clear email inbox", priority: "medium" },
      { text: "Review calendar for the week", priority: "high" },
      { text: "Update project status", priority: "medium" },
      { text: "Schedule any needed meetings", priority: "medium" },
      { text: "Clear and organize desk", priority: "low" },
    ]
  },
  {
    id: "travel-packing",
    title: "Travel Packing",
    description: "Never forget anything before a trip.",
    category: "Personal",
    icon: "✈️",
    recurring: false,
    items: [
      { text: "Passport and travel documents", priority: "high" },
      { text: "Phone charger and adapters", priority: "high" },
      { text: "Medications", priority: "high" },
      { text: "Clothes for each day", priority: "high" },
      { text: "Toiletries", priority: "high" },
      { text: "Headphones", priority: "medium" },
      { text: "Camera", priority: "low" },
      { text: "Travel insurance documents", priority: "medium" },
      { text: "Local currency or notify bank", priority: "high" },
      { text: "Download offline maps", priority: "medium" },
      { text: "Confirm hotel and transport bookings", priority: "high" },
    ]
  }
];

// ============================================
// TEMPLATE CARD
// ============================================
function TemplateCard({ template, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: selected ? "var(--accent-green-muted)" : "var(--bg-surface-2)",
        border: `2px solid ${selected ? "var(--accent-green)" : "var(--border)"}`,
        borderRadius: "var(--radius-md)",
        padding: "var(--space-4)",
        cursor: "pointer",
        transition: "border-color var(--transition-fast), background var(--transition-fast)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)"
      }}
      onMouseEnter={e => {
        if (!selected) {
          e.currentTarget.style.borderColor = "var(--accent-green)";
          e.currentTarget.style.background = "var(--bg-surface)";
        }
      }}
      onMouseLeave={e => {
        if (!selected) {
          e.currentTarget.style.borderColor = "var(--border)";
          e.currentTarget.style.background = "var(--bg-surface-2)";
        }
      }}
    >
      {/* Icon + title */}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <span style={{ fontSize: "24px" }}>{template.icon}</span>
        <div>
          <p style={{
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: "14px",
            color: "var(--text-primary)",
            margin: 0
          }}>
            {template.title}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginTop: "2px" }}>
            <CategoryTag category={template.category} size="sm" showIcon={false} />
            {template.recurring && (
              <span style={{
                fontFamily: "var(--font-body)",
                fontSize: "11px",
                color: "var(--text-muted)"
              }}>
                🔄 {template.recurringInterval}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      <p style={{
        fontFamily: "var(--font-body)",
        fontSize: "13px",
        color: "var(--text-secondary)",
        margin: 0,
        lineHeight: 1.5
      }}>
        {template.description}
      </p>

      {/* Item count */}
      <p style={{
        fontFamily: "var(--font-body)",
        fontSize: "12px",
        color: "var(--text-muted)",
        margin: 0
      }}>
        {template.items.length} items
      </p>
    </div>
  );
}

// ============================================
// ITEM PREVIEW LIST
// ============================================
function ItemPreview({ items }) {
  const PREVIEW_LIMIT = 5;
  const shown = items.slice(0, PREVIEW_LIMIT);
  const remaining = items.length - PREVIEW_LIMIT;

  const priorityColors = {
    high: "var(--priority-high)",
    medium: "var(--priority-medium)",
    low: "var(--priority-low)"
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      {shown.map((item, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-3)",
            padding: "var(--space-2) var(--space-3)",
            background: "var(--bg-surface-2)",
            borderRadius: "var(--radius-sm)",
            borderLeft: `3px solid ${priorityColors[item.priority] || "var(--border)"}`
          }}
        >
          <div style={{
            width: "16px",
            height: "16px",
            borderRadius: "4px",
            border: "2px solid var(--border-strong)",
            flexShrink: 0
          }} />
          <span style={{
            fontFamily: "var(--font-body)",
            fontSize: "13px",
            color: "var(--text-primary)"
          }}>
            {item.text}
          </span>
        </div>
      ))}
      {remaining > 0 && (
        <p style={{
          fontFamily: "var(--font-body)",
          fontSize: "12px",
          color: "var(--text-muted)",
          margin: 0,
          padding: "0 var(--space-3)"
        }}>
          + {remaining} more items
        </p>
      )}
    </div>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function TemplateModal({ onClose, onCreate }) {
  const { isGuest } = useAuth();
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");

  const selectedTemplate = TEMPLATES.find(t => t.id === selected);

  // Set title when template is selected
  function handleSelect(templateId) {
    const template = TEMPLATES.find(t => t.id === templateId);
    setSelected(templateId);
    setTitle(template?.title || "");
  }

  // ============================================
  // CREATE FROM TEMPLATE
  // ============================================
  async function handleCreate() {
    if (!selectedTemplate) return;
    setCreating(true);

    try {
      const items = selectedTemplate.items.map(item => ({
        id: crypto.randomUUID(),
        text: item.text,
        priority: item.priority,
        checked: false,
        dueDate: null,
        notes: null,
        skippedOn: null
      }));

      await onCreate({
        title: title.trim() || selectedTemplate.title,
        category: selectedTemplate.category,
        recurring: selectedTemplate.recurring,
        recurringInterval: selectedTemplate.recurringInterval || null,
        items
      });

      onClose();
    } catch (err) {
      console.error("Failed to create from template", err);
    } finally {
      setCreating(false);
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 1200,
          animation: "fadeIn 150ms ease"
        }}
      />

      {/* Modal */}
      <div style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(900px, calc(100vw - 32px))",
        maxHeight: "calc(100vh - 64px)",
        background: "var(--bg-surface)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-lg)",
        zIndex: 1201,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        animation: "fadeIn 200ms ease"
      }}>

        {/* Header */}
        <div style={{
          padding: "var(--space-5) var(--space-6)",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0
        }}>
          <div>
            <h2 style={{
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              fontSize: "22px",
              color: "var(--text-primary)",
              margin: "0 0 4px"
            }}>
              Start from a template
            </h2>
            <p style={{
              fontFamily: "var(--font-body)",
              fontSize: "14px",
              color: "var(--text-muted)",
              margin: 0
            }}>
              Pick a template to get started quickly
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: "var(--space-2)",
              cursor: "pointer",
              color: "var(--text-secondary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{
          display: "flex",
          flex: 1,
          overflow: "hidden"
        }}>

          {/* Template grid */}
          <div style={{
            flex: 1,
            padding: "var(--space-5)",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch"
          }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "var(--space-3)"
            }}>
              {TEMPLATES.map(template => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  selected={selected === template.id}
                  onClick={() => handleSelect(template.id)}
                />
              ))}
            </div>
          </div>

          {/* Preview panel */}
          {selectedTemplate && (
            <div style={{
              width: "300px",
              borderLeft: "1px solid var(--border)",
              padding: "var(--space-5)",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-4)"
            }}>
              <div>
                <p style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  margin: "0 0 var(--space-2)"
                }}>
                  Checklist Name
                </p>
                <input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  style={{
                    width: "100%",
                    fontFamily: "var(--font-body)",
                    fontSize: "14px",
                    color: "var(--text-primary)",
                    background: "var(--bg-surface-2)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-sm)",
                    padding: "var(--space-2) var(--space-3)",
                    outline: "none",
                    transition: "border-color var(--transition-fast)"
                  }}
                  onFocus={e => e.target.style.borderColor = "var(--accent-green)"}
                  onBlur={e => e.target.style.borderColor = "var(--border)"}
                />
              </div>

              <div>
                <p style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  margin: "0 0 var(--space-2)"
                }}>
                  Preview
                </p>
                <ItemPreview items={selectedTemplate.items} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "var(--space-4) var(--space-6)",
          borderTop: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0
        }}>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "13px",
            color: "var(--text-muted)",
            margin: 0
          }}>
            {selectedTemplate
              ? `${selectedTemplate.items.length} items will be added`
              : "Select a template to get started"}
          </p>

          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: "var(--space-2) var(--space-5)",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontSize: "14px",
                color: "var(--text-secondary)",
                transition: "background var(--transition-fast)"
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--bg-surface-2)"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              Cancel
            </button>

            <button
              onClick={handleCreate}
              disabled={!selectedTemplate || creating}
              style={{
                background: selectedTemplate ? "var(--accent-green)" : "var(--bg-surface-2)",
                border: "none",
                borderRadius: "var(--radius-sm)",
                padding: "var(--space-2) var(--space-5)",
                cursor: selectedTemplate ? "pointer" : "not-allowed",
                fontFamily: "var(--font-body)",
                fontSize: "14px",
                fontWeight: 600,
                color: selectedTemplate ? "white" : "var(--text-muted)",
                transition: "opacity var(--transition-fast)",
                opacity: creating ? 0.7 : 1
              }}
            >
              {creating ? "Creating..." : "Use Template"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}